import { useMutation, useQuery } from '@tanstack/react-query'
import { Clapperboard, Play, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { previewAction, previewRule, validationMessages, type ActionDef, type PreviewBundle, type RegistryKind } from '@/api/actions'
import { puppetsQuery, type PuppetFeedItem } from '@/api/puppets'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'

import { BundlePlayer } from './BundlePlayer'
import { roleTypes } from './describe'

/**
 * "Try it": pick which puppets play the parts (or let the studio choose), then play a mini story
 * that is just this action (`POST /api/actions/{id}/preview` → a bundle, R6). The request sends
 * `roles` = role name → the chosen puppet; roles left on "Choose for me" are filled by the server.
 * A world rule previews the same way (`POST /api/rules/{id}/preview`: a scene where its event
 * happens), with the studio choosing everyone.
 */

const SELECT = 'h-10 w-full rounded-xl border border-line bg-paper px-2 text-sm text-ink focus-visible:focus-ring'

export interface PreviewPanelProps {
  action: Pick<ActionDef, 'id' | 'label' | 'roles'>
  /** An action (default) or a world rule. */
  kind?: RegistryKind
  /** Start playing as soon as it mounts (the create-with-AI review). */
  autoPlay?: boolean
  /** Hide the puppet pickers (compact review). */
  compact?: boolean
  /** Play again whenever this changes (after the editor saved: the preview shows the saved version). */
  replayKey?: number
  className?: string
}

/** Roles a puppet can fill: characters first (the `cast`), then the first object role. */
export function castRoles(roles: ActionDef['roles']): { characters: string[]; object: string | null } {
  const characters: string[] = []
  let object: string | null = null
  for (const [name, spec] of Object.entries(roles ?? {})) {
    const types = roleTypes(name, spec)
    if (types.includes('character')) characters.push(name)
    else if (object === null && types.includes('object')) object = name
  }
  return { characters, object }
}

export function PreviewPanel({ action, kind = 'action', autoPlay = false, compact = false, replayKey = 0, className }: PreviewPanelProps) {
  const { characters, object } = useMemo(() => (kind === 'rule' ? { characters: [], object: null } : castRoles(action.roles)), [action.roles, kind])
  const feed = useQuery({ ...puppetsQuery({ ready: true }), enabled: !compact && kind === 'action' })
  const puppets = feed.data?.items ?? []
  const actors = puppets.filter((p) => p.kind !== 'object')
  const things = puppets.filter((p) => p.kind === 'object')
  const [picks, setPicks] = useState<Record<string, string>>({})
  const [objectPick, setObjectPick] = useState('')
  const [shown, setShown] = useState<(PreviewBundle & { key: number }) | null>(null)

  const run = useMutation({
    mutationFn: () => {
      const roles: Record<string, string> = {}
      for (const r of characters) if (picks[r]) roles[r] = picks[r]
      if (object && objectPick) roles[object] = objectPick
      return kind === 'rule' ? previewRule(action.id, { roles }) : previewAction(action.id, { roles })
    },
    onSuccess: (res) => setShown({ ...res, key: Date.now() }),
  })

  const { mutate } = run
  // Once per action (a StrictMode double effect must not make two previews).
  const autoStarted = useRef<string | null>(null)
  useEffect(() => {
    if (!autoPlay || autoStarted.current === action.id) return
    autoStarted.current = action.id
    mutate()
  }, [autoPlay, mutate, action.id])

  // A save from the editor bumps `replayKey`: play the version that was just saved.
  const replayed = useRef(replayKey)
  useEffect(() => {
    if (replayKey === replayed.current) return
    replayed.current = replayKey
    mutate()
  }, [replayKey, mutate])

  const pickers = !compact && (characters.length > 0 || object) ? (
    <div className="grid gap-3 sm:grid-cols-2">
      {characters.map((role) => (
        <PuppetSelect key={role} id={`preview-${role}`} label={`Who is the ${role.replace(/_/g, ' ')}?`} value={picks[role] ?? ''} options={actors} onChange={(v) => setPicks((p) => ({ ...p, [role]: v }))} />
      ))}
      {object ? <PuppetSelect id={`preview-${object}`} label={`What is the ${object.replace(/_/g, ' ')}?`} value={objectPick} options={things} onChange={setObjectPick} /> : null}
    </div>
  ) : null

  return (
    <section className={cn('flex flex-col gap-3', className)} aria-label="Preview" data-testid="preview-panel">
      {shown ? (
        <BundlePlayer key={shown.key} bundle={shown.bundle} bundleUrl={shown.bundleUrl} />
      ) : (
        <div className="grid aspect-video w-full place-items-center rounded-xl border border-dashed border-line bg-paper-2 text-center">
          <div className="flex flex-col items-center gap-2 px-4 text-ink-2">
            <Clapperboard className="size-8 text-ink-3" aria-hidden />
            <p className="text-sm">{run.isPending ? 'Setting the stage…' : kind === 'rule' ? `See “${action.label}” happen.` : `See “${action.label}” played by puppets.`}</p>
          </div>
        </div>
      )}
      {pickers}
      {run.error ? (
        <div className="rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-danger" role="alert">
          <p className="font-medium">The preview could not be made.</p>
          <ul className="mt-1 list-disc pl-5">
            {validationMessages(run.error).map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant={shown ? 'secondary' : 'primary'} onClick={() => run.mutate()} loading={run.isPending} data-testid="preview-play">
          {run.isPending ? null : shown ? <RotateCcw aria-hidden /> : <Play aria-hidden />}
          {shown ? 'Play again' : 'Play the preview'}
        </Button>
      </div>
    </section>
  )
}

function PuppetSelect({ id, label, value, options, onChange }: { id: string; label: string; value: string; options: PuppetFeedItem[]; onChange(v: string): void }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <select id={id} className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose for me</option>
        {options.map((p) => (
          <option key={p.uid} value={p.uid}>
            {p.name}
          </option>
        ))}
      </select>
    </div>
  )
}
